import React, { useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import Select from '../../../design-system/components/Select';
import { useForm } from '../../../hooks';
import { Button, ButtonVariant } from '../Button';
import { Icon, IconSize, IconType } from '../Icon';
import { Input } from '../Input';
import ActionsPopup from './ActionsHeader/ActionsPopup';

import * as s from '../../../design-system/components/Table/Table.module.scss';

const TablePagination = React.memo(
  React.forwardRef(({ table, itemsPerPage, rowsCount, children, className, fitScreen, userPrefsKeys, onResetColumnSorting, onResetColumnWidths, onResetColumnVisibility, onUserPrefsUpdate, ...props }, ref) => {
    const [t] = useTranslation();
    const currentPageIndex = table.getState().pagination.pageIndex + 1;
    const [data, handleFieldChange, setData] = useForm(() => ({ page: '' }));
    const pageField = useRef(null);
    const itemsPerPageOptions = ['25', '50', '100', '250', '500', '1000', 'all'].map((item) => ({
      value: item,
      label: `${item} ${t('common.perPage')}`,
    }));

    const handleItemsPerPageChange = useCallback(
      (event) => {
        const { value } = event.target;
        onUserPrefsUpdate({ [userPrefsKeys.itemsPerPage]: value });
        if (value === 'all') {
          table.setPageSize(Number.MAX_SAFE_INTEGER);
        } else {
          table.setPageSize(Number(value));
        }
      },
      [onUserPrefsUpdate, table, userPrefsKeys.itemsPerPage],
    );

    const handleSubmit = useCallback(() => {
      const pageNumber = parseInt(data.page, 10);
      const maxPage = itemsPerPage === 'all' ? 1 : Math.ceil(rowsCount / Number(itemsPerPage));
      if (Number.isNaN(pageNumber) || pageNumber < 1 || pageNumber > maxPage) {
        pageField.current?.focus();
        setData({ page: '' });
        return;
      }

      setData({ page: '' });
      table.setPageIndex(pageNumber - 1);
    }, [data.page, itemsPerPage, rowsCount, setData, table]);

    const handleCancel = useCallback(() => {
      setData({ page: '' });
    }, [setData]);

    const handleFieldKeyDown = useCallback(
      (e) => {
        switch (e.key) {
          case 'Enter':
            handleSubmit();
            break;
          case 'Escape':
            handleCancel();
            break;
          default:
        }
      },
      [handleCancel, handleSubmit],
    );

    return (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <div ref={ref} className={clsx(s.pagination, className)} {...props}>
        <Button variant={ButtonVariant.Icon} title={t('common.firstPage')} onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}>
          <Icon type={IconType.AngleLeftDouble} size={IconSize.Size13} />
        </Button>
        <Button variant={ButtonVariant.Icon} title={t('common.previousPage')} onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
          <Icon type={IconType.AngleLeft} size={IconSize.Size13} />
        </Button>
        <Input ref={pageField} name="page" placeholder={currentPageIndex} value={data.page} onKeyDown={handleFieldKeyDown} onChange={handleFieldChange} className={s.paginationPageInput} />
        <span>/{table.getPageCount() || 1}</span>
        <Button variant={ButtonVariant.Icon} title={t('common.nextPage')} onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
          <Icon type={IconType.AngleLeft} size={IconSize.Size13} className={s.paginationIconRotated} />
        </Button>
        <Button variant={ButtonVariant.Icon} title={t('common.lastPage')} onClick={() => table.setPageIndex(table.getPageCount() - 1)} disabled={!table.getCanNextPage()}>
          <Icon type={IconType.AngleLeftDouble} size={IconSize.Size13} className={s.paginationIconRotated} />
        </Button>
        <Select
          value={itemsPerPage}
          options={itemsPerPageOptions}
          onChange={handleItemsPerPageChange}
          className={s.paginationSelectControl}
          wrapperClassName={s.paginationSelect}
          aria-label={t('common.perPage')}
        />
        <ActionsPopup
          table={table}
          fitScreen={fitScreen}
          userPrefsKeys={userPrefsKeys}
          onResetColumnWidths={onResetColumnWidths}
          onResetColumnSorting={onResetColumnSorting}
          onResetColumnVisibility={onResetColumnVisibility}
          onUserPrefsUpdate={onUserPrefsUpdate}
          position="top"
          offset={4}
          hideCloseButton
          keepOnScroll
        >
          <Button variant={ButtonVariant.Icon} title={t('common.editListView')} className={s.tableSettingsButton}>
            <Icon type={IconType.Settings} size={IconSize.Size13} className={s.iconTableSettingsButton} />
          </Button>
        </ActionsPopup>
        {children}
      </div>
    );
  }),
);

TablePagination.propTypes = {
  table: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  itemsPerPage: PropTypes.string.isRequired,
  rowsCount: PropTypes.number.isRequired,
  children: PropTypes.node,
  className: PropTypes.string,
  fitScreen: PropTypes.bool.isRequired,
  userPrefsKeys: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  onResetColumnSorting: PropTypes.func.isRequired,
  onResetColumnWidths: PropTypes.func.isRequired,
  onResetColumnVisibility: PropTypes.func.isRequired,
  onUserPrefsUpdate: PropTypes.func.isRequired,
};

TablePagination.defaultProps = {
  children: undefined,
  className: undefined,
};

export default TablePagination;
